import { formatScaleFactor, RECIPE_SCALE_FACTORS } from '@platewell/core';
import { useRef, type KeyboardEvent } from 'react';

/**
 * The 1× 2× 3× 4× choice above a recipe's ingredients. It is a radio group, so only the
 * checked option takes a tab stop and the arrow keys move between options.
 */
export function ScaleSwitch({
  factor,
  onChange,
}: {
  factor: number;
  onChange: (factor: number) => void;
}) {
  const options = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const count = RECIPE_SCALE_FACTORS.length;
    const current = RECIPE_SCALE_FACTORS.indexOf(factor as (typeof RECIPE_SCALE_FACTORS)[number]);
    const next = (Math.max(current, 0) + step + count) % count;
    onChange(RECIPE_SCALE_FACTORS[next]!);
    options.current[next]?.focus();
  }

  return (
    <div
      className="mode-switch scale-switch"
      role="radiogroup"
      aria-label="Scale recipe"
      onKeyDown={onKeyDown}
    >
      {RECIPE_SCALE_FACTORS.map((option, index) => (
        <button
          key={option}
          ref={(element) => {
            options.current[index] = element;
          }}
          type="button"
          role="radio"
          aria-checked={option === factor}
          tabIndex={option === factor ? 0 : -1}
          className={`mode-switch-option ${option === factor ? 'active' : ''}`}
          onClick={() => onChange(option)}
        >
          {formatScaleFactor(option)}
        </button>
      ))}
    </div>
  );
}
